// @flow
import { DELETE_SAVE_SEARCH_REQUEST, DELETE_SAVE_SEARCH_SUCCESS } from './saveSearch';
import { getAuthToken, LOG_OUT_BIDDER } from './user';
import api from '../api/saveSearch';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import { getDeployment } from './config';
import { getUnfollowSearchAnalytics } from './analytics';
import type { GlobalState } from '../rootReducer';
import isEmpty from 'lodash/isEmpty';
import ms from 'ms';
import { pageSlicer } from '../../utils/pagination';
import { toFacetString } from './savedSearchFacets';

/* Action Types */
export const DELETE_SAVED_SEARCH_FAIL = 'la/user/savedSearches/DELETE_FAIL';
export const DELETE_SAVED_SEARCH_REQUEST = 'la/user/savedSearches/DELETE_REQUEST';
export const DELETE_SAVED_SEARCH_SUCCESS = 'la/user/savedSearches/DELETE_SUCCESS';

export const LOAD_SAVED_SEARCHES_FAIL = 'la/user/savedSearches/LOAD_FAIL';
export const LOAD_SAVED_SEARCHES_REQUEST = 'la/user/savedSearches/LOAD_REQUEST';
export const LOAD_SAVED_SEARCHES_SUCCESS = 'la/user/savedSearches/LOAD_SUCCESS';

export const SUBSCRIBE_SAVED_SEARCH_FAIL = 'la/user/savedSearches/SUBSCRIBE_FAIL';
export const SUBSCRIBE_SAVED_SEARCH_REQUEST = 'la/user/savedSearches/SUBSCRIBE_REQUEST';
export const SUBSCRIBE_SAVED_SEARCH_SUCCESS = 'la/user/savedSearches/SUBSCRIBE_SUCCESS';

export const UNSUBSCRIBE_SAVED_SEARCH_FAIL = 'la/user/savedSearches/UNSUBSCRIBE_FAIL';
export const UNSUBSCRIBE_SAVED_SEARCH_REQUEST = 'la/user/savedSearches/UNSUBSCRIBE_REQUEST';
export const UNSUBSCRIBE_SAVED_SEARCH_SUCCESS = 'la/user/savedSearches/UNSUBSCRIBE_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
const DEFAULT_STATE = {
    loaded: 0,
    loading: false,
    searches: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let searches;

    switch (action.type) {
        case LOAD_SAVED_SEARCHES_FAIL:
            return {
                ...state,
                loading: false,
            };
        case LOAD_SAVED_SEARCHES_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_SAVED_SEARCHES_SUCCESS:
            return {
                ...state,
                loaded: action.meta.actionTime,
                loading: false,
                searches: action.payload,
            };
        case DELETE_SAVED_SEARCH_REQUEST:
            searches = [];
            cloneDeep(state.searches).forEach(x => {
                if (x.savedSearchId !== action.payload) {
                    searches.push(x);
                }
            });

            return {
                ...state,
                searches,
            };
        case SUBSCRIBE_SAVED_SEARCH_REQUEST:
            searches = cloneDeep(state.searches);
            searches.forEach(x => {
                if (x.savedSearchId === action.payload) {
                    x.sendAlert = true;
                }
            });

            return {
                ...state,
                searches,
            };
        case UNSUBSCRIBE_SAVED_SEARCH_REQUEST:
            searches = cloneDeep(state.searches);
            searches.forEach(x => {
                if (x.savedSearchId === action.payload) {
                    x.sendAlert = false;
                }
            });

            return {
                ...state,
                searches,
            };
        case LOG_OUT_BIDDER:
            return DEFAULT_STATE;
        default:
            return state;
    }
}

/* SELECTORS */
const savedSearchSelector = (state: GlobalState) => state.savedSearch;
const idSelector = (state: GlobalState, id) => id;

const savedSearchSearchesSelector = createSelector(savedSearchSelector, savedSearch => savedSearch.searches);

export const getSavedSearches = createSelector(savedSearchSearchesSelector, savedSearches => savedSearches);

export const hasSavedSearchesSelector = createSelector(getSavedSearches, savedSearches => savedSearches.length > 0);

export const getSavedSearchById = createSelector([savedSearchSearchesSelector, idSelector], (savedSearches, id) =>
    savedSearches.find(x => x.savedSearchId === id)
);

export const getLoadTimeForSavedSearches = createSelector(savedSearchSelector, savedSearch => savedSearch.loaded || 0);

export const isSavedSearchesLoading = createSelector(savedSearchSelector, savedSearch => savedSearch.loading || false);

const shouldFetchSavedSearches = state => {
    const loaded = getLoadTimeForSavedSearches(state);
    const time = Date.now();
    const diff = time - loaded;
    if (diff < REDUX_STORE_TIME) return false;
    return !isSavedSearchesLoading(state);
};

// sort/ filtering
const savedSearchSortAscending = (a = {}, b = {}) => {
    const aFacetString = toFacetString(a.facets).toLowerCase();
    const bFacetString = toFacetString(b.facets).toLowerCase();
    if (bFacetString > aFacetString) {
        return -1;
    } else if (aFacetString > bFacetString) {
        return 1;
    }
    return 0;
};

const compareFunctions = {
    LAST_SENT_ASCENDING: (a = {}, b = {}) => {
        if (!a || !a.lastSent || !a.lastSent) return 1;
        if (!b || !b.lastSent || !b.lastSent) return -1;
        return a.lastSent === b.lastSent
            ? savedSearchSortAscending(a, b)
            : Date.parse(a.lastSent) - Date.parse(b.lastSent);
    },
    LAST_SENT_DESCENDING: (a = {}, b = {}) => {
        if (!a || !a.lastSent || !a.lastSent) return 1;
        if (!b || !b.lastSent || !b.lastSent) return -1;
        return a.lastSent === b.lastSent
            ? savedSearchSortAscending(a, b)
            : Date.parse(b.lastSent) - Date.parse(a.lastSent);
    },
    RECENT_ITEM_ASCENDING: (a = {}, b = {}) => {
        if (!a || !a.mostRecent || !a.mostRecent) return 1;
        if (!b || !b.mostRecent || !b.mostRecent) return -1;
        return a.mostRecent === b.mostRecent
            ? savedSearchSortAscending(a, b)
            : Date.parse(a.mostRecent) - Date.parse(b.mostRecent);
    },
    RECENT_ITEM_DESCENDING: (a = {}, b = {}) => {
        if (!a || !a.mostRecent || !a.mostRecent) return 1;
        if (!b || !b.mostRecent || !b.mostRecent) return -1;
        return a.mostRecent === b.mostRecent
            ? savedSearchSortAscending(a, b)
            : Date.parse(b.mostRecent) - Date.parse(a.mostRecent);
    },
    SEARCH_TERM_ASCENDING: (a = {}, b = {}) => {
        return savedSearchSortAscending(a, b);
    },
    SEARCH_TERM_DESCENDING: (a = {}, b = {}) => {
        return savedSearchSortAscending(b, a);
    },
};

const savedSearchSearchFilter = (wrapper, searchString = '') => {
    const searchTokens = searchString
        .toLowerCase()
        .trim()
        .split(/\s/);
    const matchingTokens = searchTokens.filter(token => {
        const facetString = toFacetString(wrapper.facets);
        return `${facetString}`.toLowerCase().includes(token);
    });
    const doesMatch = matchingTokens.length === searchTokens.length;
    return doesMatch;
};

export const getFilteredSavedSearches = (
    state: GlobalState,
    { count, filterNoResults, page, searchTerms, sort }: any = {}
) => {
    const items = getSavedSearches(state);
    const loadedItems = items.filter(item => !isEmpty(item));
    const loadedItemsCount = loadedItems.length;
    const filteredItems = searchTerms
        ? loadedItems.filter(itemWrapper => savedSearchSearchFilter(itemWrapper, searchTerms))
        : loadedItems;
    const filteredItems2 = filterNoResults ? filteredItems.filter(itemWrapper => itemWrapper.itemCount) : filteredItems;
    const sortedItems = sort ? filteredItems2.sort(compareFunctions[sort]) : filteredItems2;
    return {
        filteredCount: filteredItems.length || 0,
        items: pageSlicer(sortedItems, count, page) || [],
        loadedItemsCount,
        totalCount: items.length || 0,
    };
};

export const getSavedSearchesCount = (state: GlobalState) => {
    const items = getSavedSearches(state);

    return items.length;
};

export const createSaveSearchKeyBySearchParams = (searchParams: any): string => {
    if (
        searchParams &&
        searchParams.facets &&
        searchParams.facets[0] &&
        searchParams.facets[0].values &&
        searchParams.facets[0].values[0]
    ) {
        let key = `category_id:${searchParams.facets[0].values[0].value}`;
        return key;
    }
    return '';
};

/* ACTION CREATORS */
export const fetchSavedSearchesIfNeeded = (force: boolean = false) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    const authToken = getAuthToken(state);
    if ((force || shouldFetchSavedSearches(state)) && authToken) {
        const deployment = getDeployment(state);
        try {
            dispatch({
                type: LOAD_SAVED_SEARCHES_REQUEST,
            });
            const response = await api.fetchSavedSearches({ authToken, deployment });
            dispatch({
                meta: { actionTime: Date.now() },
                payload: response.data,
                type: LOAD_SAVED_SEARCHES_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: LOAD_SAVED_SEARCHES_FAIL,
            });
        }
    }
    return Promise.resolve();
};

export const submitDeleteSavedSearch = (savedSearch: any) => async (dispatch: Function, getState: Function) => {
    const { savedSearchId } = savedSearch;
    const state = getState();
    const authToken = getAuthToken(state);
    const deployment = getDeployment(state);
    const analytics = getUnfollowSearchAnalytics();
    try {
        dispatch({
            payload: savedSearchId,
            type: DELETE_SAVED_SEARCH_REQUEST,
        });
        dispatch({
            payload: createSaveSearchKeyBySearchParams(savedSearch),
            type: DELETE_SAVE_SEARCH_REQUEST,
        });
        const response = await api.postDeleteSavedSearch({ authToken, deployment, savedSearchId });
        dispatch({
            meta: { actionTime: Date.now(), ...analytics, savedSearchId },
            payload: response.payload,
            type: DELETE_SAVED_SEARCH_SUCCESS,
        });
        dispatch({
            payload: createSaveSearchKeyBySearchParams(savedSearch),
            type: DELETE_SAVE_SEARCH_SUCCESS,
        });
        dispatch(fetchSavedSearchesIfNeeded(true));
    } catch (error) {
        dispatch({
            error: true,
            meta: { savedSearchId },
            payload: error,
            type: DELETE_SAVED_SEARCH_FAIL,
        });
    }
};

export const toggleSavedSearchEmail = (savedSearchId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const deployment = getDeployment(state);
    const search = getSavedSearchById(state, savedSearchId);
    if (search.sendAlert) {
        dispatch({
            payload: savedSearchId,
            type: UNSUBSCRIBE_SAVED_SEARCH_REQUEST,
        });
        try {
            const response = await api.postUnsubscribeFromSavedSearch({
                authToken,
                deployment,
                savedSearchId,
            });
            dispatch({
                meta: { savedSearchId },
                payload: response.payload,
                type: UNSUBSCRIBE_SAVED_SEARCH_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                meta: { savedSearchId },
                payload: error,
                type: UNSUBSCRIBE_SAVED_SEARCH_FAIL,
            });
        }
    } else {
        dispatch({
            payload: savedSearchId,
            type: SUBSCRIBE_SAVED_SEARCH_REQUEST,
        });
        try {
            const response = await api.postSubscribeToSavedSearch({
                authToken,
                deployment,
                savedSearchId,
            });
            dispatch({
                meta: { savedSearchId },
                payload: response.payload,
                type: SUBSCRIBE_SAVED_SEARCH_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                meta: { savedSearchId },
                payload: error,
                type: SUBSCRIBE_SAVED_SEARCH_FAIL,
            });
        }
    }
};
